import { useState, useEffect, useRef } from "react";
// Redux
import { useDispatch, useSelector } from "react-redux";
//Components
import { addIdKeyIfMissing } from "@delibrandev/utils/src/function.js";
import { createAttribute, deleteItems, getAttributeFromGroup, updateAsociationAttribute, updateAttribute } from "../../../../../api/attributes";
import { setAlert } from "../../../../../reducers/userConfigSlice";
import { Button, translateMessage } from "@delibrandev/utils";
import { DraggableListVertical } from "../../../../../components/draggableListVertical";

export const sortAttributeItems = items => [...(items || [])].sort((a, b) => (Number(a.order) || 0) - (Number(b.order) || 0));
const itemSequence = items => (items || []).map(item => item.itemId).join("|");

export const AttributesList = ({ onEditorStateChange } = {}) => {
  console.log("AttributesList");
  //const dataObj = useSelector((state) => state.aplicationConfig.userConfig.floatPanel.data);
  const [data, setData] = useState([]);
  const [savedOrder, setSavedOrder] = useState("");
  const [unconfirmedOrder, setUnconfirmedOrder] = useState(false);
  const [savingOrder, setSavingOrder] = useState(false);
  const [orderMessage, setOrderMessage] = useState("");
  const [orderError, setOrderError] = useState("");
  const orderBusy = useRef(false);
  const mutationBusy = useRef(false);
  const [mutationPending, setMutationPending] = useState(false);
  const orderDirty = unconfirmedOrder || itemSequence(data.items) !== savedOrder;

  useEffect(() => { onEditorStateChange?.({ busy: savingOrder || mutationPending, dirty: orderDirty }); }, [onEditorStateChange, savingOrder, mutationPending, orderDirty]);

  const dispatch = useDispatch();
  const queryId = useSelector((state) => state.aplicationConfig.userConfig.floatPanel.queryId);
  const [mode, setMode] = useState("none");
  const [selected, updateSelected] = useState([]);

  const getQuery = async () => {
    //dispatch(setLoading(true));
    try {
      const response = await getAttributeFromGroup(queryId);
      console.log("getAttributeFromGroup: ", response);
      if (response && !response.error) {
        const items = sortAttributeItems(response.data.items);
        setData({...response.data, items});
        setSavedOrder(itemSequence(items));
      }
    } catch (error) {
      console.error("Error en la petición:", error);
    }
    //dispatch(setLoading(false));
  };

  useEffect(() => {
    getQuery();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const saveOrder = async () => {
    if (mutationBusy.current || orderBusy.current || !orderDirty) return;
    const items = [...data.items];
    orderBusy.current = true;
    setSavingOrder(true); setOrderError(""); setOrderMessage("");
    try {
      for (let order = 0; order < items.length; order++) {
        const response = await updateAsociationAttribute(items[order].itemId, {order}, queryId);
        if (!response || response.error) throw new Error("order update");
      }
      const response = await getAttributeFromGroup(queryId);
      if (!response || response.error) throw new Error("order readback");
      const confirmed = sortAttributeItems(response.data.items);
      if (itemSequence(confirmed) !== itemSequence(items)) throw new Error("order mismatch");
      setData({...response.data, items: confirmed});
      setSavedOrder(itemSequence(confirmed));
      setUnconfirmedOrder(false);
      setOrderMessage("Orden guardado");
    } catch {
      setUnconfirmedOrder(true);
      setOrderError("No se pudo confirmar el orden. Puede haberse guardado parcialmente. Tu orden se conserva; vuelve a intentarlo.");
    } finally {
      orderBusy.current = false;
      setSavingOrder(false);
    }
  };

  const reorderItems = items => {
    if (mutationBusy.current || orderBusy.current) return;
    setData(previous => ({...previous, items}));
    setOrderMessage("");
    setOrderError("");
  };

  const handleSelected = (id) => {
    console.log("handleSelected: " + id);
    updateSelected(
      (prevSelected) =>
        prevSelected.includes(id)
          ? prevSelected.filter((item) => item !== id) // Si existe, eliminar
          : [...prevSelected, id] // Si no existe, agregar
    );
  };

  const addAtributte = async () => {
    if (orderDirty || orderBusy.current || mutationBusy.current) return;
    mutationBusy.current = true; setMutationPending(true);
    try {
      console.log("addAtributte");
      const randomNumber = Math.floor(Math.random() * (9999 - 1000 + 1)) + 1000;
      const payload = {
        name: "Atributo " + randomNumber,
        price: "0",
        itemGroupId: queryId,
      };

      const response = await createAttribute(payload);
      console.log("response: ", response);
      if (response && !response.error) {
        dispatch(setAlert(translateMessage(response?.message || "Bad request")));
        console.log(response);
        await getQuery();
      } else {
        dispatch(setAlert(translateMessage(response?.message || "Bad request")));
        console.log("Error en la operación");
      }
    } catch {
      dispatch(setAlert("No se pudo completar la operación. Vuelve a intentarlo."));
    } finally { mutationBusy.current = false; setMutationPending(false); }
  };

  const deleteAtributte = async () => {
    if (orderDirty || orderBusy.current || mutationBusy.current) return;
    mutationBusy.current = true; setMutationPending(true);
    try {
      console.log("deleteAtributte");
      const payload = {
        itemIds: selected,
      };

      const response = await deleteItems(queryId, payload);
      if (response && !response.error) {
        dispatch(setAlert(translateMessage(response?.message || "Bad request")));
        setMode("none");
        updateSelected([]);
        await getQuery();
      } else {
        dispatch(setAlert(translateMessage(response?.message || "Bad request")));
        console.log("Error en la operación");
      }
    } catch {
      dispatch(setAlert("No se pudo completar la operación. Vuelve a intentarlo."));
    } finally { mutationBusy.current = false; setMutationPending(false); }
  };

  const updateItem = async (itemId, name, price, checked) => {
    if (orderDirty || orderBusy.current || mutationBusy.current) return false;
    mutationBusy.current = true; setMutationPending(true);
    try {
      const response = typeof checked === "boolean"
        ? await updateAsociationAttribute(itemId, {isActive:checked}, queryId)
        : await updateAttribute(itemId, {name,price});
      if (!response || response.error) {
        dispatch(setAlert(translateMessage(response?.message || "Bad request")));
        return false;
      }
      dispatch(setAlert(translateMessage(response.message || "Item updated successfully")));
      await getQuery();
      return true;
    } catch {
      dispatch(setAlert(translateMessage("No se pudo guardar el atributo. Comprueba la conexión y vuelve a intentarlo.")));
      return false;
    } finally { mutationBusy.current = false; setMutationPending(false); }
  };

  const ActionsDraggable = () => {
    return (
      <>
        {mode === "none" && (
          <div className="flex-end">
            <Button text={"Seleccionar"} icon={null} customClass={"customButtonColor1"} action={() => setMode("delete")} />
            <Button text={"Agregar"} icon={null} customClass={"customButtonColor2"} action={addAtributte} />
          </div>
        )}
        {mode === "delete" && (
          <div className="flex-end">
            <Button text={"Eliminar"} icon={null} customClass={"customButtonColor1"} action={deleteAtributte} />
            <Button text={"Cancelar"} icon={null} customClass={"customButtonColor2"} action={() => setMode("none")} />
          </div>
        )}
      </>
    );
  };

  return (
    <div className="row">
      <div className="col-md-12 col-sm-12 text-end mb-2">
        {!orderDirty && !savingOrder && <fieldset disabled={mutationPending} style={{border:0,padding:0,margin:0}}><ActionsDraggable /></fieldset>}
        {orderDirty && <button type="button" className="customButton customButtonColor2" disabled={savingOrder} aria-busy={savingOrder} onClick={saveOrder}>{savingOrder ? "Guardando orden…" : "Guardar orden"}</button>}
        {orderDirty && !orderError && <p role="status">Orden sin guardar. Guarda el orden antes de editar los atributos.</p>}
        {orderMessage && <p role="status">{orderMessage}</p>}
        {orderError && <p role="alert">{orderError}</p>}
      </div>
      <div className="col-md-12">
        {console.log("atributes: ", addIdKeyIfMissing(data?.items))}
        {data?.items?.length > 0 && (
          <DraggableListVertical data={addIdKeyIfMissing(data?.items)} updateItem={updateItem} mode={mode} handleSelected={handleSelected} onReorder={reorderItems} disabled={savingOrder || mutationPending} editingDisabled={orderDirty || mutationPending} />
        )}
      </div>
    </div>
  );
};
